import { axios, publicAxios, endpoints, createSSEConnection } from 'Api';

export const getAllActivePlans = async () => axios.post(endpoints.getAllActivePlans);

export const getUserDetailsFromCode = async ({ code }) => axios.post(endpoints.getUserDetFrmCode, { code });

export const getUserContents = async ({ content_limit, content_start, limit, start, user_id }) => axios.post(endpoints.getUserContents, { content_limit, content_start, limit, start, user_id });

export const addVideoDraftContent = async (body) => axios.post(endpoints.addVideoDraftContent, body);

export const addVideo = async (body) => axios.post(endpoints.addVideo, body);

export const addPhotoContent = async (body) => axios.post(endpoints.addPhotoContent, body);

export const addS3VideoDraftContent = async (body) => axios.post(endpoints.addS3VideoContent, body);

export const getVideosDetail = async ({ id }) => axios.post(endpoints.getVideosDetail, { id });

// Gift Flow APIs (using publicAxios - no token needed)
export const saveGiftUser = async ({ firstname, lastname, email, recaptchaResponse, note }) => publicAxios.post(endpoints.saveGiftUser, { firstname, lastname, email, recaptchaResponse, note });

export const getAllGiftPlans = async () => publicAxios.post(endpoints.getAllActivePlans, { show_gift_plans: true });

export const createPaymentIntent = async ({ uid, plan_id, url, test, promo }) => publicAxios.post(endpoints.createPaymentIntent, { uid, plan_id, url, test, ...(promo ? { promo } : {}) });

export const registerGiftUser = async ({ uid, checkout_id, plan_id, fromName, fromEmail, deliveryDate, promo_code }) => publicAxios.post(endpoints.registerGiftUser, { uid, checkout_id, plan_id, fromName, fromEmail, deliveryDate, promo_code });

export const updateGiftUser = async ({ uid, note, contributor_details }) => publicAxios.post(endpoints.updateGiftUser, { uid, note, ...(contributor_details ? { contributor_details } : {}) });

// Get gift status (polling/initial fetch)
export const getGiftStatus = async (uid) => publicAxios.get(`/getGiftStatus?uid=${uid}`);

// Send gift email to recipient
export const sendGiftEmail = async ({ uuid }) => publicAxios.post(endpoints.sendGiftEmail, { uuid });

// Update gift status for mobile flow - pushes real-time messages via SSE
export const updateGiftStatus = async ({ uuid, push, token, uploaderToken, mobileUpload }) => publicAxios.post(endpoints.updateGiftStatus, { uuid, push, token, uploaderToken, mobileUpload });

// Helper to parse SSE push field (shared between initial connection and reconnects)
const parseSSEMessage = (event, onMessage) => {
  console.log('SSE raw message:', event.data);
  try {
    const data = JSON.parse(event.data);

    // Parse the push field if it exists
    if (data.push && typeof data.push === 'string') {
      try {
        // The push field is a JSON-like string, but not valid JSON (uses single quotes and unquoted keys)
        // Convert it to valid JSON format first
        const jsonString = data.push
          .replace(/(\w+):/g, '"$1":') // Add quotes around keys
          .replace(/:\s*'((?:[^'\\]|\\.)*)'/g, (match, p1) => {
            const unescaped = p1.replace(/\\'/g, "'");
            const escaped = unescaped.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
            return `:"${escaped}"`;
          })
          .replace(/\['/g, '["') // Replace [' with ["
          .replace(/'\]/g, '"]') // Replace '] with "]
          .replace(/','/g, '","'); // Replace ',' with ","

        const pushData = JSON.parse(jsonString);
        data.parsedPush = pushData;
        console.log('Parsed push data:', pushData);
      } catch (pushError) {
        console.error('Error parsing push field:', pushError);
        data.parsedPush = null;
      }
    }

    if (onMessage) onMessage(data);
  } catch (err) {
    console.error('Error parsing SSE message:', err);
    if (onMessage) onMessage(event.data); // Pass raw data if not JSON
  }
};

// Gift Flow SSE - Listen to real-time upload status updates (with auto-reconnect)
// Proactively reconnects every 50s to avoid server's 60s timeout
export const subscribeToGiftUploadStatus = (uid, { onMessage, onError, onOpen }) => {
  let eventSource = null;
  let isClosed = false;
  let reconnectAttempts = 0;
  let reconnectTimer = null;
  let proactiveRefreshTimer = null;
  const MAX_RECONNECT_DELAY = 30000; // Cap at 30s
  const BASE_RECONNECT_DELAY = 1000; // Start at 1s
  const PROACTIVE_REFRESH_INTERVAL = 50000; // Refresh every 50s (before 60s server timeout)

  const connect = () => {
    if (isClosed) return;

    // Clear any existing proactive refresh timer
    if (proactiveRefreshTimer) {
      clearTimeout(proactiveRefreshTimer);
      proactiveRefreshTimer = null;
    }

    eventSource = createSSEConnection('/giftUploadStatus', { uid });

    eventSource.onopen = () => {
      console.log('[SSE] Connection opened (attempt:', reconnectAttempts, ')');
      reconnectAttempts = 0; // Reset on successful connection
      if (onOpen) onOpen();

      // Schedule proactive refresh before server timeout
      proactiveRefreshTimer = setTimeout(() => {
        if (isClosed) return;
        console.log('[SSE] Proactive refresh - reconnecting before timeout');
        if (eventSource) {
          eventSource.close();
        }
        connect();
      }, PROACTIVE_REFRESH_INTERVAL);
    };

    eventSource.onmessage = (event) => parseSSEMessage(event, onMessage);

    eventSource.onerror = (error) => {
      console.error('[SSE] Connection error:', error);
      if (onError) onError(error);

      if (isClosed) return;

      // Clear proactive refresh since we're reconnecting due to error
      if (proactiveRefreshTimer) {
        clearTimeout(proactiveRefreshTimer);
        proactiveRefreshTimer = null;
      }

      // Close the broken connection
      eventSource.close();

      // Reconnect with exponential backoff (1s, 2s, 4s, 8s, ... capped at 30s)
      reconnectAttempts += 1;
      const delay = Math.min(BASE_RECONNECT_DELAY * (2 ** (reconnectAttempts - 1)), MAX_RECONNECT_DELAY);
      console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttempts})`);
      reconnectTimer = setTimeout(connect, delay);
    };
  };

  connect();

  // Return cleanup function to close connection and stop reconnecting
  return () => {
    isClosed = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer);
    if (eventSource) eventSource.close();
  };
};
